/**
 * Document Generation API hooks
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { logger } from '@/lib/utils/logger'

// ── Types ────────────────────────────────────────────────────────────────────

export type DocumentType =
  | 'test_report'
  | 'requirements_doc'
  | 'compliance_report'
  | 'security_audit'
  | 'release_assessment'
  | 'defect_summary'
  | 'traceability_matrix'
  | 'coverage_report'
  | 'executive_summary'
  | 'risk_decision_report'
  | 'test_plan'
  | 'test_execution_report'
  | 'audit_binder'
  | 'custom'

export type OutputFormat =
  | 'pdf'
  | 'html'
  | 'markdown'
  | 'json'
  | 'docx'
  | 'xlsx'
  | 'PDF'
  | 'HTML'
  | 'Markdown'
  | 'MARKDOWN'
  | 'JSON'
  | 'DOCX'
  | 'XLSX'

export type DocumentStatus =
  | 'queued'
  | 'processing'
  | 'rendering'
  | 'uploading'
  | 'completed'
  | 'failed'
  | 'cancelled'

export interface Document {
  id: number
  tenant_id: number | string
  project_id: number | null
  title: string
  document_type: DocumentType
  requested_formats: OutputFormat[]
  status: DocumentStatus
  error_message: string | null
  input_params: Record<string, any>
  requested_by: number
  template_id: number | null
  created_at: string
  updated_at: string
  completed_at: string | null
}

export interface DocumentArtifact {
  id: number
  document_id: number
  /**
   * Current backend field.
   */
  format?: OutputFormat | string
  /**
   * Legacy/frontend alias retained for compatibility with older code paths.
   */
  output_format?: OutputFormat | string
  file_name: string
  file_size: number
  gcs_path?: string
  content_type?: string
  sha256_checksum?: string | null
  page_count?: number | null
  rendering_duration_ms?: number | null
  created_at: string
}

export interface DocumentWithArtifacts extends Document {
  artifacts: DocumentArtifact[]
}

export interface GenerateDocumentRequest {
  project_id?: number
  title: string
  document_type: DocumentType
  requested_formats: OutputFormat[]
  input_params: Record<string, any>
  template_id?: number
}

export interface DownloadResponse {
  download_url: string
  expires_in_seconds?: number
}

export interface DocumentsListResponse {
  items: Document[]
  total: number
  limit: number
  offset: number
}

export interface DocumentListParams {
  project_id?: number
  document_type?: DocumentType
  status?: DocumentStatus
  limit?: number
  offset?: number
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function normalizeOutputFormat(format?: OutputFormat | string | null): string {
  return String(format || '').trim().toLowerCase()
}

export function getArtifactFormat(artifact: DocumentArtifact): string {
  return normalizeOutputFormat(artifact.format || artifact.output_format)
}

export function isPdfArtifact(artifact: DocumentArtifact): boolean {
  const format = getArtifactFormat(artifact)
  const contentType = String(artifact.content_type || '').toLowerCase()
  const fileName = String(artifact.file_name || '').toLowerCase()

  return format === 'pdf' || contentType === 'application/pdf' || fileName.endsWith('.pdf')
}

export function isXlsxArtifact(artifact: DocumentArtifact): boolean {
  const format = getArtifactFormat(artifact)
  const contentType = String(artifact.content_type || '').toLowerCase()
  const fileName = String(artifact.file_name || '').toLowerCase()

  return (
    format === 'xlsx' ||
    contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    fileName.endsWith('.xlsx')
  )
}

export function findArtifactByFormat(
  artifacts: DocumentArtifact[] | undefined,
  requestedFormat: OutputFormat | string
): DocumentArtifact | undefined {
  const normalizedRequestedFormat = normalizeOutputFormat(requestedFormat)

  if (!artifacts?.length) {
    return undefined
  }

  if (normalizedRequestedFormat === 'pdf') {
    return artifacts.find(isPdfArtifact)
  }

  if (normalizedRequestedFormat === 'xlsx') {
    return artifacts.find(isXlsxArtifact)
  }

  return artifacts.find((artifact) => getArtifactFormat(artifact) === normalizedRequestedFormat)
}

// ── API Functions ────────────────────────────────────────────────────────────

async function generateDocument(
  request: GenerateDocumentRequest
): Promise<Document> {
  const response = await apiClient.post('/api/documents/generate', request)
  return response.data
}

async function getDocument(documentId: number): Promise<DocumentWithArtifacts> {
  const response = await apiClient.get(`/api/documents/${documentId}`)
  return response.data
}

async function listDocuments(params?: DocumentListParams): Promise<DocumentsListResponse> {
  const response = await apiClient.get('/api/documents', { params })
  return response.data
}

async function downloadArtifact(
  documentId: number,
  artifactId: number
): Promise<string> {
  const response = await apiClient.get<DownloadResponse>(
    `/api/documents/${documentId}/artifacts/${artifactId}/download`
  )
  return response.data.download_url
}

async function deleteDocument(documentId: number): Promise<void> {
  await apiClient.delete(`/api/documents/${documentId}`)
}

// ── React Query Hooks ────────────────────────────────────────────────────────

export function useGenerateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: generateDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (error) => {
      logger.error('Failed to generate document', error)
    },
  })
}

export function useDocument(documentId: number | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['documents', documentId],
    queryFn: () => getDocument(documentId!),
    enabled: !!documentId && (options?.enabled ?? true),
    refetchInterval: (query) => {
      const data = query.state.data
      if (
        data?.status === 'queued' ||
        data?.status === 'processing' ||
        data?.status === 'rendering' ||
        data?.status === 'uploading'
      ) {
        return 2000
      }
      return false
    },
  })
}

export function useDocuments(params?: DocumentListParams) {
  return useQuery({
    queryKey: ['documents', params],
    queryFn: () => listDocuments(params),
  })
}

export function useDownloadArtifact() {
  return useMutation({
    mutationFn: ({ documentId, artifactId }: { documentId: number; artifactId: number }) =>
      downloadArtifact(documentId, artifactId),
    onSuccess: (downloadUrl) => {
      window.open(downloadUrl, '_blank')
    },
    onError: (error) => {
      logger.error('Failed to download artifact', error)
    },
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (error) => {
      logger.error('Failed to delete document', error)
    },
  })
}

/**
 * Helper: Generate IEEE 830 requirements document from DRD.
 */
export async function generateRequirementsDocument(
  drd: {
    id: number
    title: string
    version: string
    project_name?: string
    project_id?: number
    test_project_id?: number
    requirements?: Array<{
      id: number
      requirement_id: string
      title?: string
      description: string
      requirement_type?: string
      priority?: string
      complexity?: string
      category?: string
    }>
    content?: {
      quality_metrics?: Record<string, any>
    }
  },
  format: OutputFormat = 'pdf'
): Promise<Document> {
  const projectId = drd.test_project_id || drd.project_id

  if (!projectId || projectId === 0) {
    throw new Error('DRD must have a valid test_project_id to generate document')
  }

  const apiFormat = normalizeOutputFormat(format) as OutputFormat

  const transformedRequirements = (drd.requirements || []).map((req, index) => ({
    id: req.requirement_id || `REQ-${String(index + 1).padStart(3, '0')}`,
    title: req.title || req.description?.split('.')[0] || 'Untitled Requirement',
    description: req.description || 'No description provided',
    priority: req.priority || 'Medium',
    inputs: `Input specifications for ${req.requirement_id || 'this requirement'}`,
    processing: `Processing logic based on ${req.requirement_type || 'functional'} requirements`,
    outputs: `Expected outputs according to ${req.category || 'system'} specifications`,
  }))

  const projectName = drd.project_name || 'Untitled Project'
  const generatedDate = new Date().toISOString()

  const request: GenerateDocumentRequest = {
    project_id: projectId,
    title: `${projectName} - Software Requirements Specification`,
    document_type: 'requirements_doc',
    requested_formats: [apiFormat],
    input_params: {
      drd_id: drd.id,
      metadata: {
        title: drd.title,
        project_name: projectName,
        version: drd.version || '1.0',
        author: 'QxProveIt AI Augmented',
        organization: 'Generated by QxProveIt',
        generated_date: generatedDate,
        report_type: 'generated-requirements',
      },
      purpose: `This Software Requirements Specification (SRS) document provides a comprehensive description of the requirements for ${projectName}. It is intended for use by developers, testers, project managers, and other stakeholders involved in the development and maintenance of the system.`,
      scope: `This document specifies the software requirements for ${projectName} as derived from canonical evidence and source artifacts. It covers functional and non-functional requirements, system constraints, and quality attributes.`,
      product_perspective: `${projectName} is designed as a modern software solution that integrates with existing infrastructure and provides governed capabilities for requirements tracking, test management, quality assurance, evidence collection, and operational reporting.`,
      definitions: [
        { term: 'DRD', definition: 'Design Requirements Document - Source document for this SRS' },
        { term: 'SRS', definition: 'Software Requirements Specification - IEEE 830-1998 standard document' },
        { term: 'Stakeholder', definition: 'Any person or organization with an interest in the system' },
      ],
      functions: [
        'Requirements management and tracking',
        'Test case generation and execution reporting',
        'Quality metrics collection and analysis',
        'Compliance verification and documentation',
        'Evidence-based operational risk reporting',
      ],
      users: [
        { role: 'Developer', characteristics: 'Technical staff implementing system features' },
        { role: 'Tester', characteristics: 'QA personnel executing test cases and validating requirements' },
        { role: 'Project Manager', characteristics: 'Management overseeing project delivery and compliance' },
        { role: 'Compliance Stakeholder', characteristics: 'Stakeholders reviewing traceability, evidence, and audit readiness' },
      ],
      requirements: transformedRequirements,
      quality_metrics: drd.content?.quality_metrics || {},
    },
  }

  return generateDocument(request)
}

/**
 * Helper: Generate a traceability matrix export for a project.
 */
export async function generateTraceabilityMatrixDocument(params: {
  project_id: number
  project_name?: string
  title?: string
  format?: OutputFormat
  input_params?: Record<string, any>
}): Promise<Document> {
  const projectName = params.project_name || 'Untitled Project'
  const apiFormat = normalizeOutputFormat(params.format || 'xlsx') as OutputFormat

  return generateDocument({
    project_id: params.project_id,
    title: params.title || `${projectName} - Traceability Matrix`,
    document_type: 'traceability_matrix',
    requested_formats: [apiFormat],
    input_params: {
      metadata: {
        project_name: projectName,
        version: '1.0',
        author: 'QxProveIt AI Augmented',
        organization: 'Generated by QxProveIt',
        generated_date: new Date().toISOString(),
        report_type: 'traceability-matrix',
      },
      ...(params.input_params || {}),
    },
  })
}
